/**
 * usePeerMediaTracks — Subscribe to remote media tracks for a specific peer.
 *
 * Returns video and audio MediaStream objects derived from the peer's remote
 * tracks. Streams are reset whenever the underlying RTCPeerConnection is
 * replaced or closed (reconnect, failure, disconnect) and are picked up from
 * the peer directly when the component mounts after the tracks arrived.
 */

import { useState, useEffect } from "react";
import { useEventBus } from "shared/services/eventBus";
import { ROBOT_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import { useConnectionManager } from "features/robot-teleop/contexts/RobotConnectionManagerContext";
import { PEER_STATES } from "features/robot-teleop/webrtc/peerStates";

const EMPTY = { remoteTracks: [], videoStream: null, audioStream: null };

// Streams synthesized for tracks that arrived without a stream (no a=msid in
// the SDP). Cached per track so that every event derives the SAME MediaStream
// object — a new object per event would re-attach srcObject and restart
// playback each time another track of the peer arrives.
const synthesizedStreams = new WeakMap();
const streamForTrack = (trackEvt) => {
  const stream = trackEvt.streams?.[0];
  if (stream) return stream;
  const track = trackEvt.track;
  let synthesized = synthesizedStreams.get(track);
  if (!synthesized) {
    synthesized = new MediaStream([track]);
    synthesizedStreams.set(track, synthesized);
  }
  return synthesized;
};

const deriveStreams = (trackEvents) => {
  const tracks = trackEvents || [];
  if (tracks.length === 0) return EMPTY;

  let videoStream = null;
  let audioStream = null;
  for (const trackEvt of tracks) {
    const track = trackEvt?.track;
    if (!track) continue;
    if (track.kind === "video" && !videoStream) {
      videoStream = streamForTrack(trackEvt);
    }
    if (track.kind === "audio" && !audioStream) {
      audioStream = streamForTrack(trackEvt);
    }
  }
  return { remoteTracks: tracks, videoStream, audioStream };
};

export const usePeerMediaTracks = (peerId) => {
  const { manager } = useConnectionManager();
  const eventBus = useEventBus();
  const [media, setMedia] = useState(EMPTY);

  useEffect(() => {
    const apply = (trackEvents) => setMedia(deriveStreams(trackEvents));

    // Sync with tracks that already arrived (component mounted after the track event)
    apply(peerId ? manager?.getPeer(peerId)?.remoteTrackEvents : []);
    if (!peerId) return undefined;

    const unsubTrack = eventBus.subscribe(
      getRobotEvent(peerId, ROBOT_EVENTS.MEDIA_TRACK_ADDED),
      ({ tracks }) => apply(tracks)
    );

    // A new attempt (CONNECTING/RECONNECTING) or a closed/failed peer
    // invalidates the previous connection's tracks. Track events of the new
    // attempt always follow its CONNECTING event, so clearing here is safe.
    const unsubState = eventBus.subscribe(
      getRobotEvent(peerId, ROBOT_EVENTS.CONNECTION_STATE),
      ({ state }) => {
        if (state !== PEER_STATES.CONNECTED) apply([]);
      }
    );

    return () => {
      if (typeof unsubTrack === 'function') unsubTrack();
      if (typeof unsubState === 'function') unsubState();
      setMedia(EMPTY);
    };
  }, [peerId, manager, eventBus]);

  return media;
};
